/* eslint-disable no-underscore-dangle */
import { createStore, applyMiddleware } from 'redux'
import { persistStore, persistReducer, createTransform } from 'redux-persist'
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2'
import { composeWithDevTools } from '@redux-devtools/extension'
import { thunk } from 'redux-thunk'
import localforage from 'localforage'
import { delegatingRootReducer, bindInjectStore } from './injectReducer'

// Create a global transform that will target all `loading` fields
const resetLoadingTransform = createTransform(
  // Called when persisting state (filters out `loading` fields)
  (inboundState, key) => {
    const newState = { ...inboundState };
    for (let field in newState) {
      if (field.toLowerCase().includes('loading')) {
        delete newState[field]; // Exclude any field containing 'loading'
      }
    }
    return newState;
  },
  // Called when rehydrating state (resets `loading` fields to false)
  (outboundState, key) => {
    const newState = { ...outboundState };
    for (let field in newState) {
      if (field.toLowerCase().includes('loading')) {
        newState[field] = false; // Set loading fields to false on rehydrate
      }
      if (field.toLowerCase().includes('inflight')) {
        newState[field] = 0; // Reset stale in-flight request counters on rehydrate
      }
    }
    return newState;
  }
);

// `translation` is persisted, so without this the boot gate would rehydrate as already-open and the
// app would paint against the previous session's cached dictionary. Every load must wait for
// GET /language to settle, so this flag is dropped on persist and forced false on rehydrate.
const dropTranslationReadyTransform = createTransform(
  (inboundState) => {
    if (typeof inboundState !== 'object' || inboundState === null) return inboundState;
    const { translationReady, ...rest } = inboundState;
    return rest;
  },
  (outboundState) => {
    if (typeof outboundState !== 'object' || outboundState === null) return outboundState;
    return { ...outboundState, translationReady: false };
  },
  { whitelist: ['translation'] }
);

// `layout.settings.layoutSettings.sidebar` open/hover is persisted, so without this a refresh
// would rehydrate the sidebar already-open (or hovered) from whatever state it was left in last
// session. Force it closed on every load; only the current session's own toggles should reopen it.
const collapseSidebarOnRehydrateTransform = createTransform(
  (inboundState) => inboundState,
  (outboundState) => {
    if (typeof outboundState !== 'object' || outboundState === null) return outboundState;
    if (!outboundState.settings?.layoutSettings?.sidebar) return outboundState;
    return {
      ...outboundState,
      settings: {
        ...outboundState.settings,
        layoutSettings: {
          ...outboundState.settings.layoutSettings,
          sidebar: {
            ...outboundState.settings.layoutSettings.sidebar,
            open: false,
            hover: false,
          },
        },
      },
    };
  },
  { whitelist: ['layout'] }
);

// Shell-only persist: feature caches (stock, reports, chats) are not restored after refresh.
const persistConfig = {
  key: 'cloud9-user',
  storage: localforage,
  stateReconciler: autoMergeLevel2,
  whitelist: ['auth', 'layout', 'translation', 'rest', 'branch'],
  transforms: [resetLoadingTransform, dropTranslationReadyTransform, collapseSidebarOnRehydrateTransform]
}

export const configureStore = () => {
  const pReducer = persistReducer(persistConfig, delegatingRootReducer)
  const enhancer = process.env.NODE_ENV === 'production'
    ? applyMiddleware(thunk)
    : composeWithDevTools(applyMiddleware(thunk))
  const store = createStore(pReducer, enhancer)
  store.persistedRootReducer = pReducer
  bindInjectStore(store)
  return store
}

export const configurePersistor = (store) => persistStore(store)
